import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Ban, Play, CreditCard, Percent, Clock, ShieldAlert, Download, XOctagon, Trash2 } from "lucide-react";
import { adminApi, adminDownload } from "@/lib/admin-api";

const PLANS = [["basic", "Basic £40"], ["pro", "Pro £50"], ["premium", "Premium £60"]];

export default function AdminGarage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState(null); // 'plan' | 'discount' | 'trial' | 'suspend'
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true); setMsg(null);
    try { await adminDownload(`/admin/garages/${id}/export`, "garage-export.zip"); setMsg("Export downloaded (data exported is also audit-logged)."); }
    catch (e) { setMsg(e.message || "Export failed"); }
    finally { setExporting(false); }
  };

  const doDelete = async (body) => {
    setBusy(true); setMsg(null);
    try {
      const res = await adminApi(`/admin/garages/${id}/delete`, { method: "POST", body: JSON.stringify(body) });
      setPanel(null);
      setMsg(`Garage permanently deleted. Personal data purged; invoices & payments retained for legal retention (Stripe: ${res.stripe}).`);
      setTimeout(() => navigate("/owner/dashboard"), 1500);
    } catch (e) {
      setMsg(e.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    adminApi(`/admin/garages/${id}`).then(setData).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, successMsg) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg(successMsg); setPanel(null); load(); }
    catch (e) { setMsg(e.message || "Action failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-midnight"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  if (!data) return null;
  const { garage, subscription, team, activity, audit = [], access_state, login_events = [], promoter, redeemed_codes = [] } = data;
  const demo = subscription.demo_mode || !subscription.stripe_subscription_id;

  return (
    <div className="min-h-screen bg-midnight">
      <div className="mx-auto max-w-4xl px-6 py-8">
        <button onClick={() => navigate("/owner/dashboard")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand"><ArrowLeft className="h-4 w-4" />Back to dashboard</button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-4xl font-bold uppercase text-white">{garage.name}</h1>
          {subscription.suspended && <span data-testid="suspended-badge" className="rounded-full bg-danger/30 px-3 py-1 text-xs font-bold text-red-300">SUSPENDED</span>}
          {(subscription.status === "canceled" || subscription.cancel_at_period_end) && (
            <span data-testid="cancelled-badge" className="rounded-full bg-amber-500/25 px-3 py-1 text-xs font-bold text-amber-200">
              {subscription.status === "canceled" ? "CANCELLED · READ-ONLY" : "CANCELLING"}
            </span>
          )}
          {demo && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white/60">DEMO MODE</span>}
        </div>
        <p className="mt-1 text-sm text-white/50">Registered {fmtDate(garage.created_at)}</p>

        {subscription.suspended && subscription.suspended_reason && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-red-200"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />Suspended: {subscription.suspended_reason}</div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-2">
          <ActionBtn icon={CreditCard} label="Change Plan" onClick={() => setPanel(panel === "plan" ? null : "plan")} testid="action-plan" />
          <ActionBtn icon={Percent} label={subscription.discount ? "Discount" : "Add Discount"} onClick={() => setPanel(panel === "discount" ? null : "discount")} testid="action-discount" />
          <ActionBtn icon={Clock} label="Extend Trial" onClick={() => setPanel(panel === "trial" ? null : "trial")} testid="action-trial" />
          {subscription.suspended
            ? <ActionBtn icon={Play} label="Reactivate" onClick={() => run(() => adminApi(`/admin/garages/${id}/reactivate`, { method: "POST" }), "Account reactivated")} testid="action-reactivate" />
            : <ActionBtn icon={Ban} label="Suspend" danger onClick={() => setPanel(panel === "suspend" ? null : "suspend")} testid="action-suspend" />}
          <ActionBtn icon={Download} label={exporting ? "Exporting…" : "Export Data"} onClick={doExport} testid="action-export" />
          <ActionBtn icon={XOctagon} label="Cancel Subscription" danger onClick={() => setPanel(panel === "cancel" ? null : "cancel")} testid="action-cancel-sub" />
          <ActionBtn icon={Trash2} label="Delete Permanently" danger onClick={() => setPanel(panel === "delete" ? null : "delete")} testid="action-delete" />
        </div>

        {msg && <p data-testid="action-msg" className="mt-3 rounded-lg bg-white/5 px-3 py-2 text-sm text-white/80">{msg}</p>}

        {panel === "cancel" && <CancelForm busy={busy} onSubmit={(body) => { if (window.confirm(body.immediate ? "Cancel the subscription IMMEDIATELY? Access ends now. Data is preserved." : "Cancel at period end? Access continues until the paid period expires. Data is preserved.")) run(() => adminApi(`/admin/garages/${id}/cancel-subscription`, { method: "POST", body: JSON.stringify(body) }), "Subscription cancellation applied"); }} />}
        {panel === "delete" && <DeleteForm busy={busy} garageName={garage.name} onExport={doExport} exporting={exporting} onSubmit={doDelete} />}

        {panel === "plan" && <PlanForm busy={busy} current={subscription} onSubmit={(body) => { if (window.confirm(`Change plan to ${body.plan} (${body.billing_period})? This updates billing.`)) run(() => adminApi(`/admin/garages/${id}/plan`, { method: "POST", body: JSON.stringify(body) }), "Plan updated"); }} />}
        {panel === "discount" && <DiscountForm busy={busy} existing={subscription.discount} onApply={(body) => { if (window.confirm("Apply this discount to the customer's billing?")) run(() => adminApi(`/admin/garages/${id}/discount`, { method: "POST", body: JSON.stringify(body) }), "Discount applied"); }} onRemove={() => { if (window.confirm("Remove the active discount?")) run(() => adminApi(`/admin/garages/${id}/discount?reason=removed by owner`, { method: "DELETE" }), "Discount removed"); }} />}
        {panel === "trial" && <TrialForm busy={busy} current={subscription} onSubmit={(body) => { if (window.confirm("Apply this trial change? The customer will be notified if email is configured.")) run(() => adminApi(`/admin/garages/${id}/trial-extend`, { method: "POST", body: JSON.stringify(body) }), "Trial extended"); }} />}
        {panel === "suspend" && <SuspendForm busy={busy} onSubmit={(body) => { if (window.confirm("Suspend this account? Their data is preserved; staff will be locked out until reactivated.")) run(() => adminApi(`/admin/garages/${id}/suspend`, { method: "POST", body: JSON.stringify(body) }), "Account suspended"); }} />}

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card title="Subscription">
            <Row label="Plan" value={`${(subscription.plan || "—").toUpperCase()} · ${subscription.billing_period}`} />
            <Row label="Status" value={subscription.status} />
            <Row label="Base MRR" value={`£${(subscription.monthly_amount_gbp ?? 0).toLocaleString()}`} />
            {subscription.discount && <Row label="Discounted MRR" value={`£${(subscription.discounted_monthly_gbp ?? 0).toLocaleString()}`} />}
            <Row label="Mode" value={demo ? "Demo (DB only)" : "Live Stripe"} />
            {subscription.trial_end && <Row label="Trial ends" value={fmtDate(subscription.trial_end)} />}
            {subscription.current_period_end && <Row label="Renews / access until" value={fmtDate(subscription.current_period_end)} />}
            {subscription.cancel_at_period_end && <Row label="Cancels at period end" value="Yes" />}
            {subscription.canceled_at && <Row label="Cancelled on" value={fmtDateTime(subscription.canceled_at)} />}
          </Card>
          <Card title="Discount">
            {subscription.discount ? (
              <>
                <Row label="Type" value={subscription.discount.type === "percent" ? `${subscription.discount.value}% off` : `£${subscription.discount.value} off`} />
                <Row label="Duration" value={subscription.discount.duration === "repeating" ? `${subscription.discount.duration_in_months} months` : subscription.discount.duration} />
                <Row label="Reason" value={subscription.discount.reason} />
                <Row label="Applied by" value={subscription.discount.applied_by || "—"} />
              </>
            ) : <p className="text-sm text-white/40">No active discount.</p>}
          </Card>
          <Card title="Contact">
            <Row label="Owner email" value={subscription.owner_email} />
            <Row label="Phone" value={garage.phone || "—"} />
            <Row label="VAT" value={garage.vat_number || "—"} />
            <Row label="Onboarded" value={garage.onboarding_complete ? "Yes" : "No"} />
          </Card>
          <Card title="Activity">
            <Row label="Customers" value={activity.customers} />
            <Row label="Vehicles" value={activity.vehicles} />
            <Row label="Jobs" value={activity.jobs} />
            <Row label="Invoices" value={activity.invoices} />
            <Row label="Lifetime invoiced" value={`£${(activity.lifetime_invoiced_gbp ?? 0).toLocaleString()}`} />
            <Row label="Lifetime collected" value={`£${(activity.lifetime_collected_gbp ?? 0).toLocaleString()}`} />
          </Card>
          <Card title={`Team (${team.length})`}>
            {team.map((m) => <Row key={m.id} label={m.name} value={m.role} />)}
          </Card>
          <Card title="Access & overview">
            <Row label="Access" value={access_state === "readonly" ? "Read-only" : access_state === "none" ? "No access" : subscription.suspended ? "Suspended" : "Full access"} />
            <Row label="Plan / status" value={`${(subscription.plan || "—").toUpperCase()} · ${subscription.status}`} />
            <Row label="Last login" value={subscription.owner_last_login ? fmtDateTime(subscription.owner_last_login) : "Never logged in"} />
            {subscription.trial_end && <Row label="Trial ends" value={fmtDate(subscription.trial_end)} />}
            {subscription.current_period_end && <Row label="Next billing" value={fmtDate(subscription.current_period_end)} />}
            {promoter ? (
              <>
                <Row label="Referred by" value={`${promoter.promoter?.name || "—"} (${promoter.code || ""})`} />
                <Row label="Commission accrued" value={`£${(promoter.commission_gbp ?? 0).toLocaleString()}`} />
              </>
            ) : <Row label="Promoter" value="—" />}
          </Card>
          <Card title="Redeemed codes">
            {redeemed_codes.length === 0 ? <p className="text-sm text-white/40">No promotion codes redeemed.</p> : redeemed_codes.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm"><span className="font-semibold text-white/80">{r.code}</span><span className="text-white/40">{r.converted_to_paid ? "paid" : "redeemed"} · {fmtDate(r.redeemed_at)}</span></div>
            ))}
          </Card>
          <Card title="Login activity">
            {login_events.length === 0 ? <p className="text-sm text-white/40">No login events recorded yet. Login history is captured going forward; events before this feature are not recoverable.</p> : login_events.slice(0, 12).map((e) => (
              <div key={e.id} className="flex items-center justify-between text-xs"><span className={e.success ? "text-white/70" : "text-red-300"}>{e.success ? "Login" : `Failed (${e.reason || "?"})`}</span><span className="text-white/40">{fmtDateTime(e.created_at)}</span></div>
            ))}
          </Card>
          <Card title="Audit history">
            {audit.length === 0 ? <p className="text-sm text-white/40">No owner actions yet.</p> : audit.map((a) => (
              <div key={a.id} className="border-b border-white/5 py-1.5 text-xs">
                <p className="font-bold text-white/80">{a.action}</p>
                <p className="text-white/40">{fmtDateTime(a.created_at)} · {a.actor_email}{a.detail?.reason ? ` · ${a.detail.reason}` : ""}</p>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-xs font-bold ${danger ? "border-danger/40 bg-danger/10 text-red-300" : "border-white/15 bg-white/5 text-white/80"}`}><Icon className="h-4 w-4" />{label}</button>
  );
}

function FormShell({ children }) {
  return <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-5">{children}</div>;
}
function Lbl({ children }) { return <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-white/50">{children}</label>; }
const inputCls = "w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-brand";

function PlanForm({ onSubmit, busy, current }) {
  const [plan, setPlan] = useState(current.plan || "basic");
  const [period, setPeriod] = useState(current.billing_period || "monthly");
  const [reason, setReason] = useState("");
  return (
    <FormShell>
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Lbl>Plan</Lbl><select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="plan-select" className={inputCls}>{PLANS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div><Lbl>Billing</Lbl><select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="plan-period" className={inputCls}><option value="monthly">Monthly</option><option value="annual">Annual</option></select></div>
        <div><Lbl>Reason</Lbl><input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="plan-reason" className={inputCls} placeholder="e.g. customer upgrade" /></div>
      </div>
      <button disabled={busy || reason.length < 3} onClick={() => onSubmit({ plan, billing_period: period, reason })} data-testid="plan-submit" className="mt-3 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">Confirm plan change</button>
    </FormShell>
  );
}

function DiscountForm({ onApply, onRemove, busy, existing }) {
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [duration, setDuration] = useState("once");
  const [months, setMonths] = useState("3");
  const [reason, setReason] = useState("");
  if (existing) {
    return <FormShell><p className="mb-3 text-sm text-white/70">Active discount: {existing.type === "percent" ? `${existing.value}% off` : `£${existing.value} off`} ({existing.reason}). Remove it before applying a new one.</p><button disabled={busy} onClick={onRemove} data-testid="discount-remove" className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm font-bold text-red-300">Remove discount</button></FormShell>;
  }
  return (
    <FormShell>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Lbl>Type</Lbl><select value={type} onChange={(e) => setType(e.target.value)} data-testid="discount-type" className={inputCls}><option value="percent">Percentage (%)</option><option value="fixed">Fixed (£ off / mo)</option></select></div>
        <div><Lbl>{type === "percent" ? "Percent off" : "£ off per month"}</Lbl><input value={value} onChange={(e) => setValue(e.target.value)} type="number" data-testid="discount-value" className={inputCls} placeholder={type === "percent" ? "20" : "10"} /></div>
        <div><Lbl>Duration</Lbl><select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="discount-duration" className={inputCls}><option value="once">Once (first invoice)</option><option value="repeating">Repeating (N months)</option><option value="forever">Forever</option></select></div>
        {duration === "repeating" && <div><Lbl>Months</Lbl><input value={months} onChange={(e) => setMonths(e.target.value)} type="number" data-testid="discount-months" className={inputCls} /></div>}
        <div className="sm:col-span-2"><Lbl>Reason</Lbl><input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="discount-reason" className={inputCls} placeholder="e.g. 50% off intro offer" /></div>
      </div>
      <p className="mt-3 rounded-lg bg-white/5 px-3 py-2 text-xs text-white/50">
        Billing effect: a Stripe coupon is attached to the live subscription, so {duration === "once" ? "the next invoice only" : duration === "repeating" ? `the next ${months} invoices` : "every future invoice"} is reduced. It does not retroactively refund past invoices and never grants a free trial.
      </p>
      <button disabled={busy || !value || reason.length < 3} onClick={() => onApply({ type, value: Number(value), duration, duration_in_months: duration === "repeating" ? Number(months) : null, reason })} data-testid="discount-submit" className="mt-3 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">Apply discount</button>
    </FormShell>
  );
}

function TrialForm({ onSubmit, busy, current }) {
  const cancelled = current?.status === "canceled";
  const currentEnd = current?.trial_end;
  const [mode, setMode] = useState("date");
  const [date, setDate] = useState("");
  const [days, setDays] = useState("7");
  const [reason, setReason] = useState("");
  if (cancelled) {
    return <FormShell><p className="text-sm text-amber-200" data-testid="trial-cancelled-note">This subscription is fully cancelled, so there is no live trial to extend. The customer should resubscribe via Checkout — their trial history is preserved and no second free trial is granted.</p></FormShell>;
  }
  const chip = (on) => `flex-1 rounded-lg border px-3 py-2 text-xs font-bold ${on ? "border-brand bg-brand/10 text-white" : "border-white/15 text-white/60"}`;
  const payload = mode === "date" ? { new_trial_end: date ? new Date(date + "T23:59:59").toISOString() : null, reason } : { days: Number(days), reason };
  const ready = reason.length >= 3 && (mode === "date" ? !!date : Number(days) > 0);
  return (
    <FormShell>
      <p className="mb-2 text-xs text-white/50">Current trial end: <span className="font-semibold text-white/80">{currentEnd ? fmtDate(currentEnd) : "— (no trial set)"}</span></p>
      <div className="mb-3 flex gap-2">
        <button onClick={() => setMode("date")} data-testid="trial-mode-date" className={chip(mode === "date")}>Pick a date</button>
        <button onClick={() => setMode("days")} data-testid="trial-mode-days" className={chip(mode === "days")}>Add days</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {mode === "date"
          ? <div><Lbl>New trial end date</Lbl><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="trial-date" className={inputCls} /></div>
          : <div><Lbl>Extra days</Lbl><input value={days} onChange={(e) => setDays(e.target.value)} type="number" data-testid="trial-days" className={inputCls} /></div>}
        <div><Lbl>Reason</Lbl><input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="trial-reason" className={inputCls} placeholder="e.g. goodwill" /></div>
      </div>
      {mode === "date" && date && <p className="mt-2 text-xs text-white/50">Proposed new trial end: <span className="font-semibold text-white/80">{fmtDate(new Date(date + "T23:59:59").toISOString())}</span></p>}
      <button disabled={busy || !ready} onClick={() => onSubmit(payload)} data-testid="trial-submit" className="mt-3 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">Extend trial</button>
    </FormShell>
  );
}

function SuspendForm({ onSubmit, busy }) {
  const [reason, setReason] = useState("");
  return (
    <FormShell>
      <Lbl>Reason for suspension</Lbl>
      <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="suspend-reason" className={inputCls} placeholder="e.g. non-payment" />
      <button disabled={busy || reason.length < 3} onClick={() => onSubmit({ reason })} data-testid="suspend-submit" className="mt-3 rounded-lg border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm font-bold text-red-300 disabled:opacity-50">Suspend account</button>
    </FormShell>
  );
}

function CancelForm({ onSubmit, busy }) {
  const [immediate, setImmediate] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <FormShell>
      <p className="mb-3 text-sm text-white/60">Cancelling never deletes data. The garage can re-subscribe later without any deletion.</p>
      <Lbl>Timing</Lbl>
      <div className="mb-3 flex gap-2">
        <button onClick={() => setImmediate(false)} data-testid="cancel-period-end" className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold ${!immediate ? "border-brand bg-brand/10 text-white" : "border-white/15 text-white/60"}`}>At period end (keeps access)</button>
        <button onClick={() => setImmediate(true)} data-testid="cancel-immediate" className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold ${immediate ? "border-danger/50 bg-danger/10 text-red-300" : "border-white/15 text-white/60"}`}>Immediately</button>
      </div>
      <Lbl>Reason</Lbl>
      <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason" className={inputCls} placeholder="e.g. customer request" />
      <button disabled={busy || reason.length < 3} onClick={() => onSubmit({ immediate, reason })} data-testid="cancel-submit" className="mt-3 rounded-lg border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm font-bold text-red-300 disabled:opacity-50">Cancel subscription</button>
    </FormShell>
  );
}

function DeleteForm({ onSubmit, onExport, exporting, busy, garageName }) {
  const [confirm, setConfirm] = useState("");
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const nameOk = confirm.trim().toLowerCase() === (garageName || "").trim().toLowerCase();
  return (
    <FormShell>
      <div className="mb-3 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-red-200">
        <p className="font-bold">Permanent deletion — this cannot be undone.</p>
        <p className="mt-1 text-red-200/80">Cancels the subscription, removes stored photos, and purges personal data (customers, vehicles, staff). Invoices &amp; payments are retained in anonymised form for UK ~6-year legal retention. A garage does NOT need deletion to rejoin — use Suspend or Cancel for that.</p>
      </div>
      <button onClick={onExport} disabled={exporting} data-testid="delete-export-first" className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-bold text-white/80 disabled:opacity-50">{exporting ? "Exporting…" : "Export data first (recommended)"}</button>
      <Lbl>Type the garage name to confirm: <span className="text-white/70">{garageName}</span></Lbl>
      <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm-name" className={inputCls} placeholder={garageName} />
      <Lbl>Reason</Lbl>
      <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="delete-reason" className={`${inputCls} mb-3`} placeholder="e.g. account closure request" />
      <label className="mb-3 flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="delete-skip-export-ack" />I have exported the data or choose to skip the export.</label>
      <button disabled={busy || !nameOk || !ack || reason.length < 3} onClick={() => onSubmit({ confirm_name: confirm.trim(), skip_export_ack: ack, reason })} data-testid="delete-submit" className="w-full rounded-lg border border-danger/50 bg-danger/20 px-4 py-2.5 text-sm font-extrabold text-red-200 disabled:opacity-40">PERMANENTLY DELETE THIS GARAGE</button>
    </FormShell>
  );
}

function Card({ title, children }) {
  return <div className="rounded-xl border border-white/10 bg-white/5 p-5"><h3 className="mb-3 font-display text-lg font-bold uppercase text-white">{title}</h3><div className="space-y-1.5">{children}</div></div>;
}
function Row({ label, value }) {
  return <div className="flex items-center justify-between text-sm"><span className="text-white/50">{label}</span><span className="font-semibold text-white">{value}</span></div>;
}
